import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, UserFocus, PencilSimple, Trash, MagnifyingGlass, Package,
} from "@phosphor-icons/react";

const emptyForm = { name: "", market_type: "", product_id: "", pain_points: "", goals: "", notes: "" };

export default function Personas() {
  const { currentOrg } = useAuth();
  const [personas, setPersonas] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pers, prods] = await Promise.all([
        api.get("/personas"),
        api.get("/products").catch(() => ({ data: [] })),
      ]);
      setPersonas(pers.data);
      setProducts((prods.data || []).filter(x => x.is_active !== false));
    } catch (err) {
      if (err?.response?.status === 403) toast.error("Personas isn't enabled for this workspace");
      else toast.error("Could not load personas");
      setPersonas([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const filtered = useMemo(() => {
    if (!query.trim()) return personas;
    const q = query.toLowerCase();
    return personas.filter(p => (p.name || "").toLowerCase().includes(q));
  }, [personas, query]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p.persona_id);
    setForm({
      name: p.name || "", market_type: p.market_type || "",
      product_id: p.product_id || "", pain_points: p.pain_points || "",
      goals: p.goals || "", notes: p.notes || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    const payload = {
      name, market_type: form.market_type || null,
      product_id: form.product_id || null,
      pain_points: form.pain_points, goals: form.goals, notes: form.notes,
    };
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/personas/${editing}`, payload);
        setPersonas(all => all.map(x => x.persona_id === editing ? data : x));
        toast.success("Persona updated");
      } else {
        const { data } = await api.post("/personas", payload);
        setPersonas(all => [data, ...all]);
        toast.success("Persona added");
      }
      setDialogOpen(false); setForm(emptyForm); setEditing(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/personas/${confirmDelete.persona_id}`);
      setPersonas(all => all.filter(x => x.persona_id !== confirmDelete.persona_id));
      toast.success(`Removed ${confirmDelete.name}`);
    } catch { toast.error("Could not remove"); }
    finally { setConfirmDelete(null); }
  };

  const marketPill = (m) => {
    if (!m) return <span className="text-ink-secondary/60">—</span>;
    const cls = m === "B2B"
      ? "border-sky-400/30 bg-sky-400/5 text-sky-200"
      : "border-fuchsia-400/30 bg-fuchsia-400/5 text-fuchsia-200";
    return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>{m}</span>;
  };

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="personas-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">Workspace</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Personas</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-lg">
            Reusable buyer archetypes — audiences, pain points and goals. Link a persona to a product,
            then reference it from any campaign brief instead of retyping.
          </p>
        </div>
        <button data-testid="personas-add" onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200">
          <Plus weight="bold" className="h-4 w-4" /> Add persona
        </button>
      </div>

      <div className="mt-6 relative max-w-md">
        <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
        <input data-testid="personas-search" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search by persona name"
          className="w-full bg-graphite-800 border border-white/10 rounded-md pl-10 pr-3 py-2 text-sm text-ink-primary placeholder:text-ink-secondary/60 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/30" />
      </div>

      <div className="mt-6 rounded-md border border-white/10 bg-graphite-900 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-ink-secondary">Loading personas…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="personas-empty">
            <UserFocus weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/50" />
            <div className="mt-4 font-display text-xl tracking-tight">
              {personas.length === 0 ? "No personas yet" : "No matches"}
            </div>
            <p className="mt-2 text-sm text-ink-secondary max-w-sm mx-auto">
              {personas.length === 0 ? "Create reusable buyer archetypes to speed up every campaign brief." : "Try a different search term."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="personas-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Market</th>
                  <th className="px-5 py-3 font-medium">Product</th>
                  <th className="px-5 py-3 font-medium">Pain points</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map(p => (
                  <tr key={p.persona_id} data-testid={`persona-row-${p.persona_id}`} className="hover:bg-white/[0.02]">
                    <td className="px-5 py-3 text-ink-primary" data-testid={`persona-name-${p.persona_id}`}>{p.name}</td>
                    <td className="px-5 py-3">{marketPill(p.market_type)}</td>
                    <td className="px-5 py-3 text-ink-secondary">
                      {p.product_name ? (
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <Package weight="duotone" className="h-3 w-3" /> {p.product_name}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-5 py-3 text-ink-secondary max-w-md">
                      <div className="line-clamp-2 text-xs">{p.pain_points || "—"}</div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button data-testid={`persona-edit-${p.persona_id}`} onClick={() => openEdit(p)}
                          className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5"><PencilSimple weight="bold" className="h-3.5 w-3.5" /></button>
                        <button data-testid={`persona-delete-${p.persona_id}`} onClick={() => setConfirmDelete(p)}
                          className="p-2 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"><Trash weight="bold" className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!saving) { setDialogOpen(v); if (!v) { setForm(emptyForm); setEditing(null); } } }}>
        <DialogContent data-testid="persona-dialog" className="max-w-2xl bg-graphite-900 border-white/10 text-ink-primary max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl tracking-tight">{editing ? "Edit persona" : "Add persona"}</DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">A reusable audience archetype — reference it from any campaign brief.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Name</label>
              <Input data-testid="persona-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} maxLength={200} className="bg-graphite-950 border-white/10" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Market type</label>
              <select data-testid="persona-form-market" value={form.market_type}
                onChange={e => setForm({ ...form, market_type: e.target.value })}
                className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                <option value="">— None —</option>
                <option value="B2B">B2B</option>
                <option value="B2C">B2C</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Product</label>
              <select data-testid="persona-form-product" value={form.product_id}
                onChange={e => setForm({ ...form, product_id: e.target.value })}
                className="w-full h-9 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
                <option value="">— None —</option>
                {products.map(p => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Pain points</label>
              <Textarea data-testid="persona-form-pain" value={form.pain_points} onChange={e => setForm({ ...form, pain_points: e.target.value })} rows={3} className="bg-graphite-950 border-white/10" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Goals</label>
              <Textarea data-testid="persona-form-goals" value={form.goals} onChange={e => setForm({ ...form, goals: e.target.value })} rows={3} className="bg-graphite-950 border-white/10" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Notes</label>
              <Textarea data-testid="persona-form-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-graphite-950 border-white/10" />
            </div>
          </div>
          <DialogFooter className="gap-2 mt-2">
            <button data-testid="persona-form-cancel" onClick={() => { if (!saving) { setDialogOpen(false); setForm(emptyForm); setEditing(null); } }} disabled={saving}
              className="rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">Cancel</button>
            <button data-testid="persona-form-save" onClick={save} disabled={saving || !form.name.trim()}
              className="rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Save changes" : "Add persona"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={v => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="persona-delete-dialog" className="bg-graphite-900 border-white/10 text-ink-primary">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove persona?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary">
              {confirmDelete?.name} will be soft-deleted. Campaigns referencing it keep their link but the persona won't appear in this list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="persona-delete-cancel" className="bg-graphite-800 border-white/10 text-ink-primary hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="persona-delete-confirm" onClick={doDelete} className="bg-red-400/90 text-graphite-950 hover:bg-red-300">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
